import { useMemo } from 'react';

import type { StandingsTable } from '@/core/engine/contracts';
import { resolveMatchRules } from '@/core/engine/matchRules';
import { standingsOf } from '@/core/engine/orchestrator';
import type { Match, MatchRuleSet, PhaseId, Team, Tournament } from '@/core/schemas';
import { toSeeds } from '@/infrastructure/services/tournamentService';

export interface PhaseView {
  readonly phaseId: PhaseId;
  readonly label: string;
  readonly matches: readonly Match[];
  readonly standings: readonly StandingsTable[];
}

/**
 * Everything the views need, computed from tournament, teams and matches.
 *
 * Nothing here is stored in Firestore: tables and brackets are recalculated
 * whenever a result arrives. That is what makes a wrong table impossible — there
 * is no second copy that could disagree with the matches.
 *
 * A failing calculation must not blank the page, so errors are captured and
 * handed back instead of thrown.
 */
export function useTournamentView(
  tournament: Tournament | undefined,
  teams: readonly Team[] | undefined,
  matches: readonly Match[] | undefined,
): { phases: PhaseView[]; error: string | null } {
  return useMemo(() => {
    if (tournament === undefined || teams === undefined || matches === undefined) {
      return { phases: [], error: null };
    }

    const participants = toSeeds(teams);

    try {
      const phases = tournament.settings.phases.map((phase) => {
        const own = matches.filter((match) => match.phaseId === phase.id);

        return {
          phaseId: phase.id,
          label: phase.label,
          matches: own,
          standings: own.length === 0 ? [] : standingsOf(tournament, phase.id, participants, own),
        };
      });

      return { phases, error: null };
    } catch (caught) {
      return {
        phases: [],
        error:
          caught instanceof Error
            ? caught.message
            : 'Die Turnierdaten konnten nicht ausgewertet werden.',
      };
    }
  }, [tournament, teams, matches]);
}

/** Resolves the mode for a single match from the tournament settings. */
export function useMatchRules(
  tournament: Tournament | undefined,
): (match: Match) => MatchRuleSet | null {
  const settings = tournament?.settings.matchRules;

  return useMemo(
    () => (match: Match) =>
      settings === undefined ? null : resolveMatchRules(settings, match.phaseId, match.roundKey),
    [settings],
  );
}
